// 📄 services/favoriteService.ts
// Favoritos del proyecto, por usuario — mismo patrón de path que ya usa
// Mi Espacio (ver miEspacioService.ts): usuarios/{uid}/projectData/{projectId}/...
// Privado por diseño (firestore.rules ya cubre esto con el wildcard
// usuarios/{uid}/{document=**}, sin necesidad de una regla nueva). Al
// estar anidados bajo projectId, un `getDocs` de esta colección ya viene
// acotado a un solo proyecto — no hace falta filtrar por projectId ni
// crear un índice compuesto.
import { collection, deleteDoc, doc, getDocs, orderBy, query, serverTimestamp, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

export type FavoriteItemType = 'solution' | 'tool' | 'space';

export interface FavoriteMetadata {
  itemType: FavoriteItemType;
  itemId: string;
  addedAt: Timestamp | null;
}

// Doc id determinístico (en vez de auto-id): agregar es idempotente (un
// mismo item nunca se puede duplicar) y quitar no necesita una query
// previa para encontrar el doc — solo doc(...).delete() directo.
const favoriteDocId = (itemType: FavoriteItemType, itemId: string) => `${itemType}_${itemId}`;

const favoritosRef = (uid: string, projectId: string) =>
  collection(db, 'usuarios', uid, 'projectData', projectId, 'favoritos');

export const getFavorites = async (uid: string, projectId: string): Promise<FavoriteMetadata[]> => {
  const q = query(favoritosRef(uid, projectId), orderBy('addedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as FavoriteMetadata);
};

export const addFavorite = async (uid: string, projectId: string, itemType: FavoriteItemType, itemId: string): Promise<void> => {
  const ref = doc(favoritosRef(uid, projectId), favoriteDocId(itemType, itemId));
  await setDoc(ref, { itemType, itemId, addedAt: serverTimestamp() });
};

export const removeFavorite = async (uid: string, projectId: string, itemType: FavoriteItemType, itemId: string): Promise<void> => {
  const ref = doc(favoritosRef(uid, projectId), favoriteDocId(itemType, itemId));
  await deleteDoc(ref);
};
